import React, { useState, useRef, useEffect } from "react";
import {
  Layers,
  Smartphone,
  User,
  MapPin,
  Calendar,
  X,
  CheckCircle2,
  Clock,
  FileText,
} from "lucide-react";
import Button from "../../common/button/button";

export interface AssociationMetaInfo {
  code?: string;
  subtype?: string;
  type?: string;
  epc?: string;
  key?: string;
  tid?: string;
}

export interface Association {
  code: string;
  metaInfo: AssociationMetaInfo;
  type: "BARCODE" | "UHF" | string;
}

export interface AdditionalData {
  association: Association[];
  addInput: any[];
  encode: any[];
}

export interface TelemetryRecord {
  _id: string;
  tenantId: string;
  userId: string;
  userName: string;
  deviceId: string;
  deviceName: string;
  processId: string;
  processName: string;
  productId: string;
  productUPC: string;
  productName?: string; // Supplemental descriptive name
  productDescription?: string;
  siteId: string;
  siteName: string;
  zoneId: string;
  zoneName: string;
  action: string;
  diId: string;
  diInfo: string;
  additionalData: AdditionalData;
  count: string;
  chunkIdentifier: string;
  finalChunk: string;
  fileUrl: string;
  status: string;
  eventTime: string;
  epcList: string;
  bizStep: string;
  disposition: string;
  readPoint: string;
  operationTime: string;
  epcisDocument: string;
  epc: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  __v: number;
}

export interface ColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  order: number;
}

export interface FilterState {
  site: string;
  zone: string;
  process: string;
  action: string;
  status: string;
  device: string;
}

export interface TimelineDataPoint {
  date: string;
  count: number;
}

export interface OperationsStats {
  total: number;
  addActions: number;
  removeActions: number;
  updateActions: number;
  activeOperators: number;
  activeDevices: number;
  processDistribution: Record<string, number>;
  timeline: TimelineDataPoint[];
}

export default function EnablementDetailDrawer({
  selectedEnablement,
  setSelectedEnablement,
}: {
  selectedEnablement: TelemetryRecord | null;
  setSelectedEnablement: React.Dispatch<
    React.SetStateAction<TelemetryRecord | null>
  >;
}) {
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const copyToClipboard = (text: string) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.top = "0";
    textArea.style.left = "0";
    textArea.style.opacity = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      if (successful) {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      }
    } catch (err) {
      console.error("Fallback copy failure", err);
    }
    document.body.removeChild(textArea);
  };

  return (
    <div className="scTheme fixed inset-y-0 right-0 z-[9999] min-h-screen bg-slate-50 text-slate-900 flex flex-col font-sans antialiased selection:bg-[var(--primary)] selection:text-white">
      {selectedEnablement && (
        <div className="fixed inset-y-0 right-0 w-full md:w-[600px] bg-white border-l border-slate-200 z-50 md:rounded-l-xl overflow-hidden shadow-[-12px_0_32px_rgba(15,23,42,0.12)] flex flex-col animate-in slide-in-from-right duration-200">
          {/* Drawer Header */}
          <div className="px-7 py-5 border-b border-slate-200 bg-white sticky top-0 flex items-start gap-3.5">
            <span className="flex h-10 w-10 flex-none items-center justify-center rounded-[10px] bg-[var(--lightBlue)] text-[var(--primary)]">
              <FileText size={20} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[18px] font-bold leading-6 text-[var(--headingTextColor)]">
                Advanced Enablement Details
              </div>
              <div className="mt-0.5 truncate text-[13px] leading-[18px] text-slate-500">
                Event, location and marker information
              </div>
            </div>
            <button
              onClick={() => setSelectedEnablement(null)}
              aria-label="Close details"
              className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focusRingSoft)]"
            >
              <X className="h-[18px] w-[18px]" />
            </button>
          </div>

          {/* Drawer Content Area */}
          <div className="px-7 py-6 space-y-6 overflow-y-auto flex-grow text-slate-700 bg-[#FCFDFE]">
            {/* Quick Summary Banner */}
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3 shadow-sm">
              <div className="flex items-center justify-between">
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
                    selectedEnablement?.action === "Add"
                      ? "bg-[var(--primaryLight)] text-[var(--primary)] border border-[rgb(var(--primaryRgb)/0.25)]"
                      : "bg-orange-50 text-orange-700 border border-orange-200"
                  }`}
                >
                  {selectedEnablement?.action} Event
                </span>
                <span className="text-[11px] font-mono text-slate-500">
                  Status:{" "}
                  <strong
                    className={`uppercase ${
                      selectedEnablement?.status?.toLowerCase() === "de-enabled"
                        ? "text-red-600"
                        : "text-emerald-600"
                    }`}
                  >
                    {selectedEnablement?.status}
                  </strong>
                </span>
              </div>
              <div>
                <h4 className="text-lg font-extrabold text-slate-900">
                  {selectedEnablement?.productName}
                </h4>

                <p className="text-xs text-slate-500 font-mono mt-0.5">
                  Description: {selectedEnablement?.productDescription || "-"}
                </p>
                <p className="text-xs text-slate-500 font-mono mt-0.5">
                  UPC Code: {selectedEnablement?.productUPC}
                </p>
              </div>
            </div>

            {/* Structured Properties */}
            <div className="space-y-4">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Location & Operator Info
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
                  <div className="text-[10px] text-slate-500 font-mono uppercase">
                    Operator
                  </div>
                  <div className="text-sm font-semibold text-slate-900 mt-1 flex items-center gap-1.5">
                    <User className="h-4 w-4 text-slate-400" />
                    {selectedEnablement?.userName}
                  </div>
                  {/* <div className="text-[10px] text-slate-500 mt-0.5 font-mono">ID: {selectedEnablement?.userId}</div> */}
                </div>

                <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
                  <div className="text-[10px] text-slate-500 font-mono uppercase">
                    Handheld Device
                  </div>
                  <div className="text-sm font-semibold text-slate-900 mt-1 flex items-center gap-1.5">
                    <Smartphone className="h-4 w-4 text-slate-400" />
                    {selectedEnablement?.deviceName}
                  </div>
                  {/* <div className="text-[10px] text-slate-500 mt-0.5 font-mono">ID: {selectedEnablement?.deviceId?.substring(0, 12)}...</div> */}
                </div>

                <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
                  <div className="text-[10px] text-slate-500 font-mono uppercase">
                    Site Terminal
                  </div>
                  <div className="text-sm font-semibold text-slate-900 mt-1 flex items-center gap-1.5">
                    <MapPin className="h-4 w-4 text-slate-400" />
                    {selectedEnablement?.siteName}
                  </div>
                  {/* <div className="text-[10px] text-slate-500 mt-0.5 font-mono">ID: {selectedEnablement?.siteId?.substring(0, 12)}...</div> */}
                </div>

                <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
                  <div className="text-[10px] text-slate-500 font-mono uppercase">
                    Target Zone
                  </div>
                  <div className="text-sm font-semibold text-slate-900 mt-1 flex items-center gap-1.5">
                    <Layers className="h-4 w-4 text-slate-400" />
                    {selectedEnablement?.zoneName}
                  </div>
                  {/* <div className="text-[10px] text-slate-500 mt-0.5 font-mono">ID: {selectedEnablement?.zoneId?.substring(0, 12)}...</div> */}
                </div>

                <div className="bg-white p-3 rounded-lg border border-slate-200 shadow-sm">
                  <div className="text-[10px] text-slate-500 font-mono uppercase">
                    Process
                  </div>
                  <div className="text-sm font-semibold text-slate-900 mt-1 flex items-center gap-1.5">
                    <Layers className="h-4 w-4 text-slate-400" />
                    {selectedEnablement?.processName}
                  </div>
                  {/* <div className="text-[10px] text-slate-500 mt-0.5 font-mono">ID: {selectedEnablement?.processId?.substring(0, 12)}...</div> */}
                </div>
              </div>
            </div>

            {/* GS1 Standard Hardware Identifiers */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Primary Marker Identifiers
              </h4>
              <div className="space-y-2.5">
                <div className="flex justify-between items-center bg-slate-50 p-3 rounded-lg border border-slate-200 text-xs">
                  <span className="text-slate-600 font-mono">Primary Tag:</span>
                  <span className="font-mono text-[var(--primary)] bg-[var(--primaryLight)] px-2 py-1 rounded border border-[rgb(var(--primaryRgb)/0.35)] font-bold">
                    {selectedEnablement?.diId}
                  </span>
                </div>
                {selectedEnablement?.epc && (
                  <div className="flex justify-between items-center bg-slate-50 p-3 rounded-lg border border-slate-200 text-xs">
                    <span className="text-slate-600 font-mono">
                      EPC Transponder Code:
                    </span>
                    <span className="font-mono text-white bg-[var(--navyBlue)] px-2 py-1 rounded border border-[var(--navyBlue)] font-bold">
                      {selectedEnablement?.epc}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center bg-slate-50 p-3 rounded-lg border border-slate-200 text-xs">
                  <span className="text-slate-600 font-mono">
                    Product UPC Code:
                  </span>
                  <span className="font-mono text-[#B8710A] bg-[#FFF1DC] px-2 py-1 rounded border border-[rgb(var(--actionOrangeRgb)/0.3)] font-bold">
                    {selectedEnablement?.productUPC}
                  </span>
                </div>
              </div>
            </div>

            {/* Nested Associations */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Active Marker Associations
              </h4>
              <div className="space-y-2">
                {selectedEnablement?.additionalData?.association.map(
                  (assoc, i) => (
                    <div
                      key={i}
                      className="p-3 bg-slate-50 rounded-lg border border-slate-200 text-xs space-y-2"
                    >
                      <div className="flex justify-between font-bold text-slate-800">
                        <span>
                          Type:{" "}
                          <strong className="text-[var(--primary)]">
                            {assoc.type}
                          </strong>
                        </span>
                        <span className="text-slate-600 font-mono">
                          Code: {assoc.code}
                        </span>
                      </div>
                      <div className="bg-white p-2 rounded text-[11px] font-mono text-slate-600 space-y-1 border border-slate-200">
                        {Object.entries(assoc.metaInfo).map(([key, val]) => (
                          <div key={key}>
                            <span className="text-[var(--primary)] font-semibold">
                              {key}:
                            </span>{" "}
                            {val}
                          </div>
                        ))}
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>

            {/* Time Metrics block */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                Audit Log Timeline Metrics
              </h4>
              <div className="p-3.5 bg-slate-50 rounded-lg border border-slate-200 space-y-3 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-600 flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" /> Handheld Operation Time
                  </span>
                  <span className="font-mono text-slate-800 font-medium">
                    {new Date(
                      selectedEnablement.operationTime,
                    ).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between border-t border-slate-200 pt-2.5">
                  <span className="text-slate-600 flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" /> Database Created At
                  </span>
                  <span className="font-mono text-slate-800 font-medium">
                    {new Date(selectedEnablement.createdAt).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between border-t border-slate-200 pt-2.5">
                  <span className="text-slate-600 flex items-center gap-1">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Last Updated At
                  </span>
                  <span className="font-mono text-slate-800 font-medium">
                    {new Date(selectedEnablement.updatedAt).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            {/* EPCIS Document Raw Structure */}
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Enablement Document Payload
                </h4>
                <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded font-mono border border-slate-200">
                  JSON
                </span>
              </div>
              <pre className="p-4 bg-[var(--navyBlue)] border border-[var(--navyBlue)] rounded-lg text-[10px] font-mono overflow-x-auto text-blue-100 max-h-60 leading-relaxed shadow-inner">
                {JSON.stringify(selectedEnablement, null, 2)}
              </pre>
            </div>
          </div>

          {/* Drawer Footer Actions */}
          <div className="px-7 py-4 border-t border-slate-200 bg-white flex gap-3">
            <button
              onClick={() =>
                copyToClipboard(JSON.stringify(selectedEnablement, null, 2))
              }
              className="flex-1 flex justify-center items-center gap-2 py-2 bg-white hover:border-[var(--primary)] hover:text-[var(--primary)] hover:bg-[var(--primaryLight)] text-slate-700 text-xs font-semibold rounded-lg transition-colors border border-slate-300 shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focusRingSoft)]"
            >
              {copySuccess ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <span className="text-emerald-700">Copied!</span>
                </>
              ) : (
                "Copy Complete Record JSON"
              )}
            </button>
            <Button
              variant="accent"
              size="sm"
              className="px-5"
              onClick={() => setSelectedEnablement(null)}
            >
              Close Details
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
